import { Button } from '@domgen/base-design-system-2.0/atoms/Button';
import { TextLink } from '@domgen/base-design-system-2.0/atoms/TextLink';
import { gaECommerceEvent, gaEvent, gaPageView } from '@domgen/shared/services/ga/ga';
import { formatLocalisedDate } from '@domgen/shared/utils/date/formatLocalisedDate';
import { parseLocalDateString } from '@domgen/shared/utils/date/parseLocalDateString';
import { log } from '@domgen/shared/utils/log';
import { usePostHog } from '@posthog/react';
import { useCallback, useEffect } from 'react';
import pdfCarePlus from '../../assets/documents/HIS_USWTCCP_0626_31_100.pdf';
import pdfReplacementCare from '../../assets/documents/HIS_USWTCRC_0626_03_100.pdf';
import { StoreEvent } from '../../enums/storeEvent';
import { QuoteState } from '../../models/store/quoteState';
import { capturePostHogEvent } from '../../services/posthog/capture';
import { useAppState } from '../../store/selectors/useAppState';
import { useConfirmation } from '../../store/selectors/useConfirmation';
import { useGaEventData } from '../../store/selectors/useGaEventData';
import { useQuote } from '../../store/selectors/useQuote';
import { useStoreContext } from '../../store/selectors/useStoreContext';
import { useUrlParams } from '../../store/selectors/useUrlParams';
import { store } from '../../store/store';
import { getDiscountDisplayData } from '../../utils/discounts/getDiscountDisplayData';
import { getSystemCode } from '../../utils/getSystemCode';
import { isReplacementCare } from '../../utils/store/isReplacementCare';
import { getConfirmationEventProperties } from '../../utils/posthog/getConfirmationEventProperties';
import { ConfirmationUpsellPanel } from './components/ConfirmationUpsellPanel';
import { formatPurchasedPlan, formatRegistrationData } from './utils/formatConfirmationPanels';
import { ConfirmationListItem } from './ConfirmationPage.types';

const styles = {
  container: 'grid grid-cols-12 gap-4 items-center p-space-500 bg-neutral-0',
  panel: 'col-span-12 w-2xl h-auto p-5 rounded-(--border-radius) bg-neutral-0 m-2 self-center',
  descriptionList: 'flex flex-col gap-2 mt-4 w-full',
  descriptionListItemTitle: 'text-body-small-strong mb-space-100',
  descriptionListItem:
    'w-full text-body-small inline-flex justify-start items-center gap-1 mb-2 bg-neutral-0 border border-inset border-card-border-neutral rounded-radius-100 px-space-200 py-space-100',
  article:
    'rounded-radius-200 bg-bg-main border border-card-border-neutral p-space-500 w-full h-fit',
};

/**
 * The confirmation caters for 4 different scenarios for displaying upsell panels and banners:
 * 1. Registration only - no quote (no upsell) - the user has registered their appliance and no offer was made to them.
 * 2. Registration only - quote (upsell) - the user has registered their appliance and an offer was made to them but not taken up.
 *    - 2a. User is displayed upsell for subs.
 *    - 2b. User is displayed upsell for replacement care.
 *    - 2c. User is displayed upsell for subs with a discount.
 * 3. Registration and purchase - no quote (no upsell) - the user has registered their appliance and taken up an offer.
 * 4. Registration and purchase - replacement care quote (no upsell) - the user has registered their appliance and taken up an offer.
 */

export function ConfirmationPage() {
  const { VITE_CLIENT_FRIENDLY_NAME, VITE_BRAND_CODE } = import.meta.env;

  const confirmation = useConfirmation();
  const { session } = useAppState();
  const urlParams = useUrlParams();
  const posthog = usePostHog();
  const gaEventData = useGaEventData();
  const storeContext = useStoreContext();

  const systemCode = getSystemCode(urlParams?.rs);
  const salesWidgetClicked = gaEventData?.salesWidgetClicked;

  const quote = useQuote() as QuoteState | undefined;

  if (!confirmation || !confirmation.applianceDetails) {
    // TODO: May want specific event for this
    store.send({ type: StoreEvent.CHECKOUT_FAILED });
    return;
  }
  const hasReplacementCare = isReplacementCare(storeContext);
  const pageName = `olr-confirmation${hasReplacementCare ? '-term' : ''}`;
  const orderPageName = `olr-order-confirmation${hasReplacementCare ? '-term' : ''}`;

  const appSessionID = session?.id;
  const registerOnly = confirmation.regOnly;
  const { applianceName, categoryName, modelNumber, serialNumber, arrivalDate, purchasePrice } =
    confirmation.applianceDetails;

  const { discountCode, startDate, netFirstPaymentAmount, itemId, endDate } =
    confirmation.offerData ?? {};
  const {
    discountTermFromPurchase,
    discountCodeFromPurchase,
    mappedPaymentType,
    transactionId,
    planNumber,
    netFirstPaymentAmountFromPurchase,
    netFirstPaymentAmountAfterPromotionalPeriodFromPurchase,
  } = confirmation.purchaseData ?? {};

  const discountTerms =
    quote && quote.data && quote.data.offer ? getDiscountDisplayData(quote.data) : undefined;

  const confirmationUpsellPrice = hasReplacementCare
    ? netFirstPaymentAmount
    : discountTerms?.netFirstPaymentAmount;

  useEffect(() => {
    if (registerOnly) {
      registerOnlyGaEvents();
      capturePostHogEvent(posthog, 'registration-only-order-confirmation-loaded', storeContext);
    } else {
      purchasePriceGaEvents();
      capturePostHogEvent(
        posthog,
        'registration-upsell-order-confirmation-loaded',
        storeContext,
        getConfirmationEventProperties(storeContext),
      );
    }
  }, []);

  const formattedArrivalDate = arrivalDate
    ? formatLocalisedDate(parseLocalDateString(arrivalDate), 'en-US')
    : '';
  const formattedStartDate = startDate
    ? formatLocalisedDate(parseLocalDateString(startDate), 'en-US')
    : '';
  const formattedEndDate = endDate
    ? formatLocalisedDate(parseLocalDateString(endDate), 'en-US')
    : '';

  log.debug({
    message: 'Confirmation page data',
    data: {
      VITE_CLIENT_FRIENDLY_NAME,
      applianceName,
      modelNumber,
      serialNumber,
      formattedArrivalDate,
    },
  });

  const registration = formatRegistrationData(
    VITE_CLIENT_FRIENDLY_NAME,
    applianceName,
    modelNumber,
    serialNumber,
    formattedArrivalDate,
  );

  const purchasePlan = confirmation.purchaseData
    ? formatPurchasedPlan(
        VITE_CLIENT_FRIENDLY_NAME,
        applianceName,
        formattedStartDate,
        formattedEndDate,
        planNumber,
        netFirstPaymentAmountFromPurchase,
        netFirstPaymentAmountAfterPromotionalPeriodFromPurchase,
        discountTermFromPurchase,
        hasReplacementCare,
      )
    : null;

  const offerStatus = confirmation.offerData ? '1' : '0';

  const registerOnlyGaEvents = () => {
    gaPageView({
      event: 'spa-pageview',
      pagename: pageName,
      category: 'registration-pages',
      type: 'STANDARD',
      appSessionID: session?.id,
      regSystemCode: systemCode,
      applianceName,
      applianceCategory: categoryName,
      offerPrice: netFirstPaymentAmount,
      appliancePurchaseCost: purchasePrice,
      appliancePurchaseDate: formattedArrivalDate,
      modelNumberDetail: modelNumber,
      serialNumberDetail: serialNumber,
      serialNumber: serialNumber ? 'provided' : 'not provided',
      modelNumber: modelNumber ? 'provided' : 'not provided',
      promotionApplied: discountCodeFromPurchase ?? discountCode,
      offerStatus,
      salesWidgetClicked,
    });
    gaEvent({
      event: 'registrationStored',
      eventLabel: '1',
      pagename: pageName,
      category: 'registration-pages',
      type: 'STANDARD',
      appSessionID: session?.id,
      regSystemCode: systemCode,
      applianceName,
      applianceCategory: categoryName,
      offerPrice: netFirstPaymentAmount,
      appliancePurchaseCost: purchasePrice,
      appliancePurchaseDate: formattedArrivalDate,
      modelNumberDetail: modelNumber,
      serialNumberDetail: serialNumber,
      serialNumber: serialNumber ? 'provided' : 'not provided',
      modelNumber: modelNumber ? 'provided' : 'not provided',
      promotionApplied: discountCodeFromPurchase ?? discountCode,
      registrationStored: '1',
      offerStatus,
      salesWidgetClicked,
    });

    if (discountTerms && registerOnly) {
      gaEvent({
        eventCategory: 'appliance details form USA',
        eventAction: 'Reg Confirmation Sales Widget',
        eventLabel: 'Offer Presented',
        pagename: pageName,
        category: 'registration-pages',
        type: 'STANDARD',
        regSystemCode: systemCode,
        applianceName,
        applianceCategory: categoryName,
        appliancePurchaseCost: purchasePrice,
        appliancePurchaseDate: formattedArrivalDate,
        modelNumberDetail: modelNumber,
        serialNumberDetail: serialNumber,
        serialNumber: serialNumber ? 'provided' : 'not provided',
        modelNumber: modelNumber ? 'provided' : 'not provided',
        offerPrice: netFirstPaymentAmount,
        appSessionID: session?.id,
        promotionApplied: discountCodeFromPurchase ?? discountCode,
        quoteId: confirmation?.offerData?.offerReference,
      });
    }
  };

  const purchasePriceGaEvents = () => {
    gaPageView({
      event: 'spa-pageview',
      pagename: orderPageName,
      category: 'registration-pages',
      type: 'STANDARD',
      appSessionID: session?.id,
      regSystemCode: systemCode,
      applianceName,
      applianceCategory: categoryName,
      offerPrice: netFirstPaymentAmount,
      appliancePurchaseCost: purchasePrice,
      appliancePurchaseDate: formattedArrivalDate,
      modelNumberDetail: modelNumber,
      serialNumberDetail: serialNumber,
      serialNumber: serialNumber ? 'provided' : 'not provided',
      modelNumber: modelNumber ? 'provided' : 'not provided',
      promotionApplied: discountCodeFromPurchase ?? discountCode,
      paymentType: mappedPaymentType === 'cc' ? 'credit card' : 'bank account',
      planNumber,
      salesWidgetClicked,
      quoteId: confirmation?.offerData?.offerReference,
    });

    gaECommerceEvent({
      pagename: orderPageName,
      category: 'registration-pages',
      type: 'STANDARD',
      event: 'purchase',
      regSystemCode: systemCode,
      version: 'WiB',
      domainId: VITE_CLIENT_FRIENDLY_NAME.replaceAll(' ', '').toUpperCase(),
      brand: VITE_BRAND_CODE.trim(),
      country: 'USA',
      customerType: 'G',
      offerStatus,
      item_promotion_name: discountCodeFromPurchase ?? discountCode,
      appSessionID,
      applianceName,
      promotionApplied: discountCodeFromPurchase ?? discountCode,
      applianceCategory: categoryName,
      salesWidgetClicked,
      quoteId: confirmation?.offerData?.offerReference,
      ecommerce: {
        currency: 'USD',
        value: netFirstPaymentAmountFromPurchase,
        transaction_id: transactionId,
        items: [
          {
            item_appliance_purchase_cost: Number(purchasePrice),
            item_appliance_purchase_date: formattedArrivalDate, // USA format date
            item_appliance_name: applianceName,
            item_appliance_warranty: true,
            item_brand: VITE_CLIENT_FRIENDLY_NAME,
            item_category: categoryName ?? '',
            item_id: itemId,
            item_name: `${VITE_CLIENT_FRIENDLY_NAME} ${applianceName}`,
            item_promotion_name: discountCodeFromPurchase ?? discountCode,
            item_model_number_detail: modelNumber,
            item_payment_type: mappedPaymentType === 'cc' ? 'credit card' : 'bank account',
            price: netFirstPaymentAmountFromPurchase,
            quantity: 1,
            item_reg_system_code: systemCode,
            item_serial_number_detail: serialNumber,
          },
        ],
      },
    });
  };

  const registerAnotherAppliance = () => {
    capturePostHogEvent(posthog, 'register-another-appliance-clicked', storeContext);

    gaEvent({
      event: 'genericGAEvent',
      eventCategory: 'appliance details form USA',
      eventAction: 'CTA clicked',
      eventLabel: 'Register another product',
      pagename: registerOnly ? pageName : orderPageName,
      category: 'registration-pages',
      type: 'STANDARD',
      regSystemCode: systemCode,
      applianceName,
      applianceCategory: categoryName,
      appSessionID: session?.id,
      ...(confirmation.offerData?.offerReference && {
        quoteId: confirmation.offerData.offerReference,
      }),
    });

    store.send({ type: StoreEvent.RESTART_JOURNEY });
  };

  const applianceCare = (
    <div className="flex flex-col text-left">
      <p className="my-4">{purchasePlan?.applianceCareTerms}</p>
      <TextLink
        href={`${hasReplacementCare ? pdfReplacementCare : pdfCarePlus}`}
        target="_blank"
        rel="noopener noreferrer"
      >
        {purchasePlan?.applianceCareTermsLinkText}
      </TextLink>
    </div>
  );

  const listItem = (listItem: ConfirmationListItem) => (
    <div key={`${listItem.label}-${listItem.value}`}>
      <dt className={styles.descriptionListItemTitle}>{listItem.label}</dt>
      <dd className={styles.descriptionListItem}>
        <span className="mr-2 text-(--color-icon-list-decorative)">
          {typeof listItem.icon === 'string' ? (
            <img src={listItem.icon} alt={listItem.label} height={16} width={16} />
          ) : (
            listItem.icon
          )}
        </span>
        {listItem.value}
      </dd>
    </div>
  );

  const upsellBanner = () => {
    const hasDiscount = !!(
      discountTerms?.discountPaymentType &&
      discountTerms?.discountPercentage &&
      discountTerms?.discountTerm
    );

    const bannerText =
      hasDiscount && !hasReplacementCare
        ? `Get a discounted price on ${VITE_CLIENT_FRIENDLY_NAME} Care Plus today - limited time only!`
        : `Get ${VITE_CLIENT_FRIENDLY_NAME} ${hasReplacementCare ? 'Replacement Care today!' : 'Care Plus today!'}`;

    return (
      <div className="bg-button-bg-primary-default  text-button-text-primary-default w-full text-center py-space-200 px-space-600">
        <p className="text-body-large-strong">{bannerText}</p>
      </div>
    );
  };

  const handleCheckoutNavigation = useCallback(() => {
    capturePostHogEvent(posthog, 'confirmation-upsell-clicked', storeContext);

    gaEvent({
      eventCategory: 'appliance details form USA',
      eventAction: 'Reg Confirmation Sales Widget ',
      eventLabel: 'CTA Clicked',
      pagename: pageName,
      category: 'registration-pages',
      type: 'STANDARD',
      regSystemCode: systemCode,
      applianceName,
      applianceCategory: categoryName,
      appliancePurchaseCost: purchasePrice,
      appliancePurchaseDate: formattedArrivalDate,
      modelNumberDetail: modelNumber,
      serialNumberDetail: serialNumber,
      serialNumber: serialNumber ? 'provided' : 'not provided',
      modelNumber: modelNumber ? 'provided' : 'not provided',
      offerPrice: netFirstPaymentAmount,
      appSessionID: session?.id,
      promotionApplied: discountCodeFromPurchase ?? discountCode,
      quoteId: confirmation?.offerData?.offerReference,
    });

    store.send({
      type: StoreEvent.CONTINUE_TO_CHECKOUT,
      data: { continueToCheckoutFromModal: false },
    });
  }, []);

  return (
    <>
      {quote?.data && upsellBanner()}
      <div className={styles.container}>
        <header className="col-start-1 col-span-12 text-left tablet:text-center">
          <h1 className="mb-space-500">That's it, you're all set!</h1>
          {!registerOnly && (
            <p className="mb-space-100">
              Registration and protection confirmed. We'll send you a confirmation email shortly.
            </p>
          )}
        </header>

        <div className="col-span-12 flex justify-center">
          <div className="w-full max-w-178 grid grid-cols-1 md:grid-cols-2 gap-space-300">
            {registration && (
              <article className={`${registerOnly ? 'md:col-span-2' : ''} ${styles.article}`}>
                <h2>{registration.panelTitle}</h2>
                <dl className={styles.descriptionList}>
                  {registration.data.map((item) => listItem(item))}
                </dl>
                <div className={`mt-space-300 ${registerOnly ? 'desktop:max-w-1/2' : ''}`}>
                  <Button variant="outline" onClick={registerAnotherAppliance}>
                    Register another product
                  </Button>
                </div>
              </article>
            )}

            {purchasePlan && !registerOnly && (
              <article className={styles.article}>
                <h2>{purchasePlan.panelTitle}</h2>
                <dl className={styles.descriptionList}>
                  {purchasePlan.data.map((item) => listItem(item))}
                </dl>
                {applianceCare}
              </article>
            )}
          </div>
        </div>
      </div>

      <div className="bg-neutral-0 w-full flex justify-center py-space-500">
        {registerOnly && discountTerms && applianceName && (
          <ConfirmationUpsellPanel
            brandName={VITE_CLIENT_FRIENDLY_NAME}
            applianceName={applianceName}
            discountPaymentAmount={confirmationUpsellPrice ?? netFirstPaymentAmount ?? 0}
            discountPaymentType={discountTerms.discountPaymentType}
            discountTerm={discountTerms.discountTerm}
            discountPercentage={discountTerms.discountPercentage}
            handleCheckoutNavigation={handleCheckoutNavigation}
            isReplacementCare={Boolean(hasReplacementCare)}
          />
        )}
      </div>
    </>
  );
}
